import { useMemo } from "react";
import { BaseballTable } from "../../components/tables/BaseballTable";
import { DataControls } from "../../components/data/DataControls";
import ErrorDisplay from "../../components/alerts/ErrorDisplay";
import { InfoBanner } from "../../components/data/InfoBanner";
import { ALLOWED_DATA_TYPES } from "./constants";
import { useGutsData } from "./useGutsData";

const BANNER_CONFIG = {
  guts: {
    title: "League Constants",
    description: "The underlying weights and scaling factors that power our advanced metrics. These league-wide constants — including linear weights for wOBA, run environment values, and replacement-level baselines — are recalculated each season and form the foundation of stats like WAR, wRC+, and FIP.",
  },
  park_factors: {
    title: "Park Factors",
    description: "Venue-level adjustments that account for how each ballpark influences run scoring. Park factors are used to normalize stats so players in hitter-friendly or pitcher-friendly environments can be compared fairly.",
  },
  expected_runs: {
    title: "Expected Runs",
    description: "The run expectancy matrix showing the average number of runs expected to score from each base-out state. These values drive context-dependent metrics like RE24 and WPA.",
  },
};

const DEFAULT_BANNER = {
  title: "League Constants",
  description: "Weights, park factors, and run expectancy tables used to calculate advanced metrics.",
};

export const Guts = () => {
  const {
    dataType, setDataType,
    selectedYears, setSelectedYears,
    searchTerm, setSearchTerm,
    division, setDivision,
    conference, setConference,
    conferences,
    columns,
    filteredData,
    loading,
    error,
    showError,
    isInitialized,
    fetchData,
  } = useGutsData();

  const banner = useMemo(() => BANNER_CONFIG[dataType] || DEFAULT_BANNER, [dataType]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      <div className="container max-w-6xl mx-auto px-8 sm:px-12 lg:px-16 py-16">
        <InfoBanner
          title={banner.title}
          description={banner.description}
        />

        <div className="relative z-10 mb-6">
          <DataControls
            dataType={dataType}
            selectedYears={selectedYears}
            minPA={0}
            minIP={0}
            searchTerm={searchTerm}
            conference={conference}
            division={division}
            selectedListId=""
            setDataType={setDataType}
            setSelectedYears={setSelectedYears}
            setMinPA={() => {}}
            setMinIP={() => {}}
            setSearchTerm={setSearchTerm}
            setConference={setConference}
            setDivision={setDivision}
            setSelectedListId={() => {}}
            conferences={conferences}
            allowedDataTypes={ALLOWED_DATA_TYPES}
            onApplyFilters={fetchData}
          />
        </div>

        <div className="relative z-10">
          {!isInitialized ? (
            <div className="flex flex-col justify-center items-center h-64">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-gray-600">Initializing...</p>
            </div>
          ) : error && showError ? (
            <div className="text-center py-12">
              <div className="bg-white/70 backdrop-blur-lg rounded-2xl border border-white/20 shadow-xl p-6 max-w-2xl mx-auto">
                <ErrorDisplay
                  error={{ message: error, status: 0 }}
                  context={{ division, dataType }}
                  onRetry={fetchData}
                  onSwitchToDivision3={null}
                />
              </div>
            </div>
          ) : (
            <div className="relative">
              {loading && (
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-20 flex items-center justify-center rounded-lg">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-2" />
                    <p className="text-sm text-gray-600">Loading data...</p>
                  </div>
                </div>
              )}

              <div className="bg-white p-3 sm:p-4 md:p-6 rounded-xl shadow-sm border border-gray-200">
                {filteredData.length === 0 && !loading ? (
                  <div className="text-center py-6 sm:py-12">
                    <p className="text-gray-500 text-base sm:text-lg">
                      No data found for the current filters.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto -mx-3 sm:mx-0">
                    <BaseballTable
                      data={filteredData}
                      columns={columns}
                      filename={`${dataType}_${division}.csv`}
                      stickyColumns={[0, 1]}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
